// Profile page (both roles): account, driver fleet, availability, push.
import React from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Truck, Send, Plus, X } from "lucide-react";
import api, { apiErrorMessage, API_BASE } from "@/lib/api";
import { US_STATES } from "@/lib/states";
import { getPushState, enablePush, disablePush, sendTestPush, pushSupported } from "@/lib/push";
import { useAuth } from "@/context/AuthContext";
import { DatePicker, F, TextInput } from "./shared";

// ---------- Profile (both roles) ----------
export function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    phone: user?.phone || "",
    bio: "",
    truck_year: "",
    truck_make: "",
    truck_model: "",
    max_length_ft: "",
    max_weight_lbs: "",
    hitch_classes: "",
    gear: "",
    alerts_enabled: true,
    sms_enabled: false,
  });
  const [driverInfo, setDriverInfo] = useState(null);
  const [corridors, setCorridors] = useState([]);
  const [availability, setAvailability] = useState("available");
  const [blackouts, setBlackouts] = useState([]);
  const [pushState, setPushState] = useState(pushSupported() ? "loading" : "unsupported");
  const [feedPath, setFeedPath] = useState("");
  const [feedBusy, setFeedBusy] = useState(false);
  const [shipperPhone, setShipperPhone] = useState(user?.phone || "");
  const [shipperSms, setShipperSms] = useState(!!user?.sms_enabled);
  const [shipperSaving, setShipperSaving] = useState(false);

  const saveShipperAlerts = async (e) => {
    e.preventDefault();
    if (shipperSaving) return;
    setShipperSaving(true);
    try {
      await api.patch("/users/me", { phone: shipperPhone.trim(), sms_enabled: !!shipperSms });
      toast.success("Text alert settings saved.");
      await refresh();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setShipperSaving(false);
    }
  };

  useEffect(() => {
    if (pushSupported()) {
      getPushState().then(setPushState).catch(() => setPushState("available"));
    }
  }, []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.role === "driver") {
      api
        .get(`/drivers/${user.id}`)
        .then((r) => {
          setDriverInfo(r.data);
          setForm((f) => ({
            ...f,
            bio: r.data.bio || "",
            phone: r.data.phone || user.phone || "",
            truck_year: r.data.truck_year ?? "",
            truck_make: r.data.truck_make || "",
            truck_model: r.data.truck_model || "",
            max_length_ft: r.data.max_length_ft ?? "",
            max_weight_lbs: r.data.max_weight_lbs ?? "",
            hitch_classes: (r.data.hitch_classes || []).join(", "),
            gear: (r.data.gear || []).join(", "),
            alerts_enabled: r.data.alerts_enabled !== false,
            sms_enabled: !!r.data.sms_enabled,
          }));
          setCorridors(r.data.corridors || []);
          setAvailability(r.data.availability_status || "available");
          setBlackouts(r.data.blackout_ranges || []);
        })
        .catch(() => {});
      api
        .get("/drivers/me/calendar-feed")
        .then((r) => setFeedPath(r.data.feed_path))
        .catch(() => {});
    }
  }, [user]);

  const regenerateFeed = async () => {
    if (feedBusy) return;
    if (!window.confirm("Regenerate your calendar link? Any calendar using the old link stops updating.")) return;
    setFeedBusy(true);
    try {
      const { data } = await api.post("/drivers/me/calendar-feed/regenerate");
      setFeedPath(data.feed_path);
      toast.success("New calendar link generated — old link is dead.");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setFeedBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (user.role !== "driver") return;
    setSaving(true);
    try {
      await api.patch("/drivers/me", {
        phone: form.phone,
        bio: form.bio,
        truck_year: form.truck_year ? parseInt(form.truck_year, 10) : null,
        truck_make: form.truck_make,
        truck_model: form.truck_model,
        max_length_ft: form.max_length_ft ? parseInt(form.max_length_ft, 10) : null,
        max_weight_lbs: form.max_weight_lbs ? parseInt(form.max_weight_lbs, 10) : null,
        hitch_classes: form.hitch_classes
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        gear: form.gear
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        alerts_enabled: !!form.alerts_enabled,
        sms_enabled: !!form.sms_enabled,
        corridors: corridors.filter((c) => c.from_state && c.to_state),
        availability_status: availability,
        blackout_ranges: blackouts.filter((r) => r.start && r.end),
      });
      toast.success("Profile saved.");
      await refresh();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-6 md:px-10 py-10 md:py-14 max-w-3xl mx-auto" data-testid="profile-page">
      <div className="mb-8">
        <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Your account</span>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-2 leading-none">Profile</h1>
      </div>
      <div className="border border-white/[0.08] bg-[#0F0F10] p-6 space-y-2">
        <Row k="Name" v={user.name} />
        <Row k="Email" v={user.email} />
        <Row k="Role" v={user.role} />
        {driverInfo && (
          <>
            <Row k="Verified" v={driverInfo.verified ? "Yes" : "Pending"} />
            <Row k="Completed jobs" v={driverInfo.completed_jobs} />
            <Row k="Rating" v={driverInfo.rating_avg ? `${driverInfo.rating_avg} (${driverInfo.rating_count})` : "No ratings yet"} />
          </>
        )}
      </div>

      {user.role === "shipper" && (
        <form onSubmit={saveShipperAlerts} className="mt-8" data-testid="shipper-alerts-form">
          <section className="border border-white/[0.08] bg-[#0F0F10] p-6 space-y-4">
            <div>
              <h2 className="font-display text-xl font-black uppercase">Text alerts</h2>
              <p className="text-sm text-zinc-500 mt-1">
                Get an SMS the moment a driver bids on your haul, plus pickup and delivery updates.
              </p>
            </div>
            <TextInput label="Phone" name="shipper_phone" value={shipperPhone} onChange={(e) => setShipperPhone(e.target.value)} testid="shipper-phone" />
            <label className="flex items-center gap-3 cursor-pointer" data-testid="shipper-sms-toggle-label">
              <input
                type="checkbox"
                checked={shipperSms}
                onChange={(e) => setShipperSms(e.target.checked)}
                data-testid="shipper-sms-toggle"
                className="w-5 h-5 accent-[#FF3B30]"
              />
              <div>
                <div className="font-semibold text-sm">Text me about my hauls</div>
                <div className="text-xs text-zinc-500">
                  New bids, pickup confirmation, and delivery notice.
                  {!shipperPhone.trim() && (
                    <span className="text-amber-400 block mt-0.5" data-testid="shipper-sms-no-phone-hint">
                      Add a phone number above so texts can reach you.
                    </span>
                  )}
                </div>
              </div>
            </label>
            <button
              type="submit"
              disabled={shipperSaving}
              data-testid="shipper-alerts-save"
              className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D62828] disabled:opacity-60 text-white font-bold uppercase tracking-widest border-2 border-[#FF3B30] hover:border-[#D62828] py-3 px-6 transition-all text-sm"
            >
              {shipperSaving ? "Saving…" : "Save text alerts"}
            </button>
          </section>
        </form>
      )}

      {user.role === "driver" && (
        <form onSubmit={save} className="mt-8 space-y-6" data-testid="driver-profile-form">
          <section>
            <h2 className="font-display text-xl font-black uppercase mb-4">Contact</h2>
            <div className="space-y-4">
              <TextInput label="Phone" name="phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} testid="profile-phone" />
              <F label="Bio">
                <textarea
                  value={form.bio}
                  onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                  rows={4}
                  placeholder="Rig, corridors you serve, insurance carrier, years hauling…"
                  data-testid="profile-bio"
                  className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-4"
                />
              </F>
            </div>
          </section>

          <section className="border-t border-white/[0.06] pt-6">
            <h2 className="font-display text-xl font-black uppercase mb-4">Fleet</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <TextInput label="Truck year" name="truck_year" type="number" value={form.truck_year} onChange={(e) => setForm((f) => ({ ...f, truck_year: e.target.value }))} testid="fleet-year" />
              <TextInput label="Truck make" name="truck_make" value={form.truck_make} onChange={(e) => setForm((f) => ({ ...f, truck_make: e.target.value }))} testid="fleet-make" />
              <TextInput label="Truck model" name="truck_model" value={form.truck_model} onChange={(e) => setForm((f) => ({ ...f, truck_model: e.target.value }))} testid="fleet-model" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <TextInput label="Max trailer length (ft)" name="max_length_ft" type="number" value={form.max_length_ft} onChange={(e) => setForm((f) => ({ ...f, max_length_ft: e.target.value }))} testid="fleet-length" />
              <TextInput label="Max tow weight (lbs)" name="max_weight_lbs" type="number" value={form.max_weight_lbs} onChange={(e) => setForm((f) => ({ ...f, max_weight_lbs: e.target.value }))} testid="fleet-weight" />
            </div>
            <div className="mt-4">
              <F label="Hitch classes (comma-separated)">
                <input
                  value={form.hitch_classes}
                  onChange={(e) => setForm((f) => ({ ...f, hitch_classes: e.target.value }))}
                  placeholder="Class IV, Class V, Gooseneck"
                  data-testid="fleet-hitch"
                  className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-4"
                />
              </F>
            </div>
            <div className="mt-4">
              <F label="Gear (comma-separated)">
                <input
                  value={form.gear}
                  onChange={(e) => setForm((f) => ({ ...f, gear: e.target.value }))}
                  placeholder="Boat launch straps, Spare bearings, Chocks"
                  data-testid="fleet-gear"
                  className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-4"
                />
              </F>
            </div>
          </section>

          <section className="border-t border-white/[0.06] pt-6" data-testid="availability-section">
            <h2 className="font-display text-xl font-black uppercase mb-2">Availability</h2>
            <p className="text-xs text-zinc-500 mb-4">
              Shippers see this on your bids and your driver passport.
            </p>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Status</label>
            <select
              value={availability}
              onChange={(e) => setAvailability(e.target.value)}
              data-testid="availability-status"
              className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white p-3 mb-5"
            >
              <option value="available">Available</option>
              <option value="on_the_road">On the road</option>
              <option value="unavailable">Not available</option>
            </select>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Blackout dates</label>
            <div className="space-y-2">
              {blackouts.map((r, i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`blackout-row-${i}`}>
                  <div className="flex-1">
                    <DatePicker
                      value={r.start}
                      onChange={(v) => setBlackouts((bs) => bs.map((x, j) => (j === i ? { ...x, start: v } : x)))}
                      testid={`blackout-start-${i}`}
                      placeholder="Start"
                      compact
                    />
                  </div>
                  <ArrowRight className="w-4 h-4 text-zinc-600 shrink-0" />
                  <div className="flex-1">
                    <DatePicker
                      value={r.end}
                      onChange={(v) => setBlackouts((bs) => bs.map((x, j) => (j === i ? { ...x, end: v } : x)))}
                      testid={`blackout-end-${i}`}
                      placeholder="End"
                      compact
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setBlackouts((bs) => bs.filter((_, j) => j !== i))}
                    data-testid={`blackout-remove-${i}`}
                    aria-label="Remove blackout range"
                    className="shrink-0 border border-white/10 text-zinc-500 hover:text-[#FF3B30] hover:border-[#FF3B30]/50 p-3 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setBlackouts((bs) => [...bs, { start: "", end: "" }])}
              data-testid="blackout-add"
              className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#FF3B30] hover:text-white border border-[#FF3B30] hover:bg-[#FF3B30] px-4 py-2.5 transition-all"
            >
              <Plus className="w-3.5 h-3.5" /> Add blackout range
            </button>
          </section>

          <section className="border-t border-white/[0.06] pt-6" data-testid="calendar-feed-section">
            <h2 className="font-display text-xl font-black uppercase mb-2">Calendar feed</h2>
            <p className="text-xs text-zinc-500 mb-4">
              Subscribe in Google Calendar, Apple Calendar, or Outlook — accepted hauls and blackout dates sync automatically.
            </p>
            <div className="flex gap-2 flex-wrap">
              <input
                readOnly
                value={feedPath ? `${API_BASE}${feedPath}` : "Loading…"}
                onFocus={(e) => e.target.select()}
                data-testid="calendar-feed-url"
                className="flex-1 min-w-[220px] bg-[#141416] border border-white/[0.08] text-zinc-300 text-xs p-3 truncate"
              />
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(`${API_BASE}${feedPath}`);
                  toast.success("Calendar link copied — paste it into your calendar app.");
                }}
                disabled={!feedPath}
                data-testid="calendar-copy"
                className="text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 bg-[#FF3B30] hover:bg-[#ff5044] text-white transition-colors disabled:opacity-50"
              >
                Copy link
              </button>
              <button
                type="button"
                onClick={regenerateFeed}
                disabled={feedBusy || !feedPath}
                data-testid="calendar-regenerate"
                className="text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 border border-white/15 text-zinc-300 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors disabled:opacity-50"
              >
                Regenerate
              </button>
            </div>
            <p className="text-[10px] text-zinc-600 mt-2">
              The link is secret to you — regenerating kills the old one instantly.
            </p>
          </section>

          <section className="border-t border-white/[0.06] pt-6" data-testid="corridors-section">
            <h2 className="font-display text-xl font-black uppercase mb-2">Corridors</h2>
            <p className="text-xs text-zinc-500 mb-4">
              Only get pinged for hauls on lanes you actually run. Leave empty to get every ping.
            </p>
            <div className="space-y-3">
              {corridors.map((c, i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`corridor-row-${i}`}>
                  <select
                    value={c.from_state}
                    onChange={(e) =>
                      setCorridors((cs) => cs.map((x, j) => (j === i ? { ...x, from_state: e.target.value } : x)))
                    }
                    data-testid={`corridor-from-${i}`}
                    className="flex-1 bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white p-3"
                  >
                    <option value="">From…</option>
                    {US_STATES.map((s) => (
                      <option key={s.abbr} value={s.abbr}>{s.abbr} — {s.name}</option>
                    ))}
                  </select>
                  <ArrowRight className="w-4 h-4 text-zinc-600 shrink-0" />
                  <select
                    value={c.to_state}
                    onChange={(e) =>
                      setCorridors((cs) => cs.map((x, j) => (j === i ? { ...x, to_state: e.target.value } : x)))
                    }
                    data-testid={`corridor-to-${i}`}
                    className="flex-1 bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white p-3"
                  >
                    <option value="ANY">Anywhere</option>
                    {US_STATES.map((s) => (
                      <option key={s.abbr} value={s.abbr}>{s.abbr} — {s.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setCorridors((cs) => cs.filter((_, j) => j !== i))}
                    data-testid={`corridor-remove-${i}`}
                    aria-label="Remove corridor"
                    className="shrink-0 border border-white/10 text-zinc-500 hover:text-[#FF3B30] hover:border-[#FF3B30]/50 p-3 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setCorridors((cs) => [...cs, { from_state: "", to_state: "ANY" }])}
              data-testid="add-corridor"
              className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#FF3B30] hover:text-white border border-[#FF3B30] hover:bg-[#FF3B30] px-4 py-2.5 transition-all"
            >
              <Plus className="w-3.5 h-3.5" /> Add corridor
            </button>
          </section>

          <section className="border-t border-white/[0.06] pt-6">
            <h2 className="font-display text-xl font-black uppercase mb-4">Alerts</h2>
            <label className="flex items-center gap-3 cursor-pointer" data-testid="alerts-toggle-label">
              <input
                type="checkbox"
                checked={form.alerts_enabled}
                onChange={(e) => setForm((f) => ({ ...f, alerts_enabled: e.target.checked }))}
                data-testid="alerts-toggle"
                className="w-5 h-5 accent-[#FF3B30]"
              />
              <div>
                <div className="font-semibold text-sm">Notify me about new hauls</div>
                <div className="text-xs text-zinc-500">
                  Email + real-time app toast the moment a new request drops.
                </div>
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer mt-4" data-testid="sms-toggle-label">
              <input
                type="checkbox"
                checked={form.sms_enabled}
                onChange={(e) => setForm((f) => ({ ...f, sms_enabled: e.target.checked }))}
                data-testid="sms-toggle"
                className="w-5 h-5 accent-[#FF3B30]"
              />
              <div>
                <div className="font-semibold text-sm">Text me when I win a haul</div>
                <div className="text-xs text-zinc-500">
                  SMS to the phone number above the moment a shipper accepts your bid.
                  {!form.phone && (
                    <span className="text-amber-400 block mt-0.5" data-testid="sms-no-phone-hint">
                      Add a phone number above so texts can reach you.
                    </span>
                  )}
                </div>
              </div>
            </label>
          </section>

          <button
            type="submit"
            disabled={saving}
            data-testid="save-profile"
            className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D62828] disabled:opacity-60 text-white font-bold uppercase tracking-widest border-2 border-[#FF3B30] hover:border-[#D62828] py-3.5 px-6 transition-all"
          >
            {saving ? "Saving…" : "Save profile"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      )}

      <section className="border-t border-white/[0.06] pt-6" data-testid="push-section">
            <h2 className="font-display text-xl font-black uppercase mb-2">Phone push</h2>
            <p className="text-xs text-zinc-500 mb-4">
              Real browser notifications — instant matches, counters, and won bids reach you with the tab closed.
            </p>
            {pushState === "unsupported" && (
              <p className="text-xs text-zinc-500">This browser doesn't support push notifications.</p>
            )}
            {pushState === "denied" && (
              <p className="text-xs text-red-400" data-testid="push-denied">
                Notifications are blocked — allow them in your browser's site settings, then come back.
              </p>
            )}
            {pushState === "available" && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    await enablePush();
                    setPushState("enabled");
                    toast.success("Push enabled — you're reachable anywhere.");
                  } catch (err) {
                    toast.error(apiErrorMessage(err, "Could not enable push"));
                    setPushState(await getPushState());
                  }
                }}
                data-testid="push-enable"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#FF3B30] hover:text-white border border-[#FF3B30] hover:bg-[#FF3B30] px-4 py-2.5 transition-all"
              >
                Enable push notifications
              </button>
            )}
            {pushState === "enabled" && (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-black bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 px-3 py-1.5" data-testid="push-enabled-badge">
                  Enabled
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    await sendTestPush();
                    toast.success("Test push sent — watch your notifications.");
                  }}
                  data-testid="push-test"
                  className="text-[10px] uppercase tracking-widest font-bold text-zinc-400 hover:text-white border border-white/10 hover:border-white px-3 py-1.5 transition-colors"
                >
                  Send test
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await disablePush();
                    setPushState("available");
                    toast.success("Push disabled.");
                  }}
                  data-testid="push-disable"
                  className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 hover:text-red-400 px-2 py-1.5"
                >
                  Disable
                </button>
              </div>
            )}
      </section>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between border-b border-white/[0.06] py-2 last:border-none">
      <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold">{k}</span>
      <span className="text-sm font-semibold text-white capitalize">{v}</span>
    </div>
  );
}
